import { useState, useEffect, useRef } from 'react';

import classnames from 'classnames';

import { track } from '@helsenorge/framework-utils/amplitude';
import { GittTilbakemelding, StartetChat } from '@helsenorge/framework-utils/amplitude/ampli';

import { shouldShowChatbotButton } from './logic';
import { ConversationStore, ButtonStore } from './store';

import styles from './styles.module.scss';

interface ChatPanelEvent {
  detail: Record<string, string>;
}
interface BoostChatPanel {
  addEventListener: (event: string, listener: (event: ChatPanelEvent) => unknown) => void;
  show: () => void;
}

declare global {
  interface Window {
    boostChatPanel?: (options: Record<string, boolean | string | string[] | null>) => BoostChatPanel;
  }
}

interface ChatbotButtonProps {
  chatPanelApiUrl: string;
  chatbotButtonText: string;
  chatbotButtonIcon: string;
  removeChatbotButtonText: string;
  chatbotFilter?: string;
  chatbotTitle: string;
  landmarkLabel: string;
}

const SCRIPT_ID = 'boostai-chatpanel';
const CHATPANEL_SRC = '/external/chatpanel-12.6.2.min.js';

const loadChatpanelScript = (): Promise<void> => {
  if (window.boostChatPanel) {
    return Promise.resolve();
  }

  const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    return new Promise<void>((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load chatpanel.js')), { once: true });
    });
  }

  return new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.async = true;
    s.defer = true;
    s.src = CHATPANEL_SRC;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load chatpanel.js'));
    document.body.appendChild(s);
  });
};

const ChatbotButton: React.FunctionComponent<ChatbotButtonProps> = ({
  chatPanelApiUrl,
  chatbotButtonText,
  chatbotButtonIcon,
  removeChatbotButtonText,
  chatbotFilter = 'privatperson',
  chatbotTitle,
  landmarkLabel,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [showButton, setShowButton] = useState(false);
  const [chatPanel, setChatPanel] = useState<BoostChatPanel>();

  useEffect(() => {
    ConversationStore.checkExpiration();
    // eslint-disable-next-line react-hooks/set-state-in-effect -- shouldShowChatbotButton bruker window som ikke finnes under SSR
    setShowButton(shouldShowChatbotButton());
  }, []);

  const handleHide = (): void => {
    ButtonStore.hide();
    setShowButton(false);
  };

  /**
   * The chatPanel.js script creates the global window-variable 'boostChatPanel'.
   * This method populates this variable with the static configuration in 'CHAT_PANEL_CONFIGURATION'
   * and url to boost.ai's API. Also sets event listeners for the chatPanel object.
   */
  const createChatPanel = async (): Promise<BoostChatPanel | undefined> => {
    if (chatPanel) {
      return chatPanel;
    }

    await loadChatpanelScript();

    if (window.boostChatPanel) {
      const chatPanelObject = window.boostChatPanel({
        hyperlinksTargetBlank: true,
        pace: 'supersonic',
        zIndex: '100000',
        filterValues: [chatbotFilter],
        title: chatbotTitle,
        apiUrlBase: chatPanelApiUrl,
        conversationId: ConversationStore.getId(),
      });

      ['chatPanelClosed', 'chatPanelMinimized'].forEach(eventName => {
        chatPanelObject.addEventListener(eventName, () => {
          buttonRef.current?.focus();
        });
      });
      chatPanelObject.addEventListener('conversationIdChanged', event => {
        ConversationStore.start(event.detail.conversationId);
      });

      chatPanelObject.addEventListener('positiveConversationFeedbackGiven', () => {
        track(
          new GittTilbakemelding({
            handling: 'gi tilbakemelding',
            objekt: 'chat',
            tilbakemeldingTekst: 'ja',
          })
        );
      });

      chatPanelObject.addEventListener('negativeConversationFeedbackGiven', () => {
        track(
          new GittTilbakemelding({
            handling: 'gi tilbakemelding',
            objekt: 'chat',
            tilbakemeldingTekst: 'nei',
          })
        );
      });

      setChatPanel(chatPanelObject);
      return chatPanelObject;
    }
  };

  const handleShowChat = async (): Promise<void> => {
    const panel = chatPanel ?? (await createChatPanel());
    panel?.show();

    track(
      new StartetChat({
        handling: 'få hjelp',
        objekt: 'chat',
      })
    );
  };

  if (!showButton) {
    return null;
  }

  return (
    <aside className={classnames(styles.chatbot, 'd-print-none')} aria-label={landmarkLabel}>
      <button ref={buttonRef} onClick={handleShowChat} type="button" className={styles.chatbot__button}>
        <img src={chatbotButtonIcon} className={styles.chatbot__icon} alt="" />
        {chatbotButtonText}
      </button>
      <button onClick={handleHide} type="button" aria-label={removeChatbotButtonText} className={styles.chatbot__close}>
        <span className={classnames(styles.close, styles.close__one)}></span>
        <span className={classnames(styles.close, styles.close__two)}></span>
      </button>
    </aside>
  );
};

export default ChatbotButton;
